import z from "zod";
import { PriceType } from "@/openapi/generated";

const optionalNumber = (val: unknown): number | undefined => {
  if (val === "" || val === null || val === undefined) return undefined;
  if (typeof val === "number") return Number.isFinite(val) ? val : undefined;
  if (typeof val === "string") {
    const parsed = Number(val);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const transitionNumber = (val: unknown): number => {
  if (val === "" || val === null || val === undefined) return 0;
  if (typeof val === "number") return Number.isFinite(val) ? val : 0;
  if (typeof val === "string") {
    const parsed = Number(val);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const variantColumnSchema = z.object({
  participantCount: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  doNotUse: z.boolean(),
  duration: z.preprocess(optionalNumber, z.number().optional()),
  transitionMinutes: z.preprocess(transitionNumber, z.number()),
  payRate: z.preprocess(optionalNumber, z.number().optional()),
  price: z.preprocess(optionalNumber, z.number().optional()),
  priceType: z.preprocess(
    (val) =>
      val === "" || val === null || val === undefined ? undefined : val,
    z.nativeEnum(PriceType).optional(),
  ),
  onlineMeetingUrl: z.string().nullable().optional(),
});

export const variantColumnsTuple = z.tuple([
  variantColumnSchema,
  variantColumnSchema,
  variantColumnSchema,
]);

export type VariantColumnSchemaType = z.infer<typeof variantColumnSchema>;

function isBlankOrValidUrl(value: string | null | undefined): boolean {
  if (value == null) return true;
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return z.string().url().safeParse(trimmed).success;
}

export function addVariantColumnIssues(
  columns: [
    VariantColumnSchemaType,
    VariantColumnSchemaType,
    VariantColumnSchemaType,
  ],
  ctx: z.RefinementCtx,
): void {
  columns.forEach((column, index) => {
    const active = index === 0 || !column.doNotUse;
    if (!active) return;
    const path = ["columns", index] as const;

    if (column.duration == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Duration is required",
        path: [...path, "duration"],
      });
    } else if (column.duration <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Duration must be greater than 0",
        path: [...path, "duration"],
      });
    }

    if (column.transitionMinutes < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Transition time must be 0 or greater",
        path: [...path, "transitionMinutes"],
      });
    }

    if (column.payRate != null && column.payRate < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pay rate must be 0 or greater",
        path: [...path, "payRate"],
      });
    }

    if (column.price != null && column.price < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Price must be 0 or greater",
        path: [...path, "price"],
      });
    }

    if (!column.priceType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Price type is required",
        path: [...path, "priceType"],
      });
    }

    if (!isBlankOrValidUrl(column.onlineMeetingUrl)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Invalid URL format",
        path: [...path, "onlineMeetingUrl"],
      });
    }
  });
}
